import { useUser } from "src/common/context/userContextUtils.js";
import { useGetReportStatusQuery } from "src/common/redux/api/exopenApi.js";
import { COMPANYADMIN } from "src/common/service/users.js";
import { useAiChatAccess } from "src/features/ai-chat/hooks/useAiChatAccess.js";
import { useGetCompanyModulesQuery } from "src/features/planning/api/planningApi.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import { useIntelligenceLayerVisible } from "src/features/products/useIntelligenceLayerVisible.js";
import { useHasExcelReportAccess } from "src/features/excelReports/hooks/useHasExcelReportAccess.js";
import { useGetPowerBiConnectionStatusQuery } from "src/features/powerBiReports/api/powerBiReportsApi.js";
import { getProductAccess, type ProductAccess } from "./productAccess.js";
import {
	type ExcelTarget,
	getExcelTarget,
	type ProductKey,
} from "./products.js";

/** Connected form of getProductAccess for the current domain and user. */
export const useProductAccess = (): {
	access: Record<ProductKey, ProductAccess>;
	/** Where every "Exopen for Excel" entry point leads; null when the domain has neither v7 nor EWR. */
	excelTarget: ExcelTarget | null;
	/** The domain's EWR address, also for v7 users — the report picker still links to it. */
	ewrUrl: string | null;
	/** Per product, so one slow lookup doesn't put every tile into a loading state. */
	loading: { ai: boolean; planning: boolean; excel: boolean; powerBi: boolean };
	/** Entitlement unknown rather than absent — never offer activation on this. */
	failed: { excel: boolean; powerBi: boolean };
} => {
	const companyDomain = useLoadedCompanyDomain();
	const { hasPermissions } = useUser();
	const aiChat = useAiChatAccess(companyDomain.id);
	const { data: planningModules, isLoading: isLoadingModules } =
		useGetCompanyModulesQuery({ companyId: companyDomain.id });
	const {
		data: reportStatus,
		isLoading: isLoadingReportStatus,
		isError: isReportStatusError,
	} = useGetReportStatusQuery({
		companyDomainId: companyDomain.id,
	});
	const intelligenceLayerVisible = useIntelligenceLayerVisible();
	const hasExcelReportAccess = useHasExcelReportAccess(companyDomain.id);
	// WHY currentData: `data` keeps the previous domain's status after a domain
	// switch (and through a failed request on the new one), which would show
	// the next domain as connected.
	const {
		currentData: powerBiStatus,
		isFetching: isFetchingPowerBi,
		isError: isPowerBiError,
	} = useGetPowerBiConnectionStatusQuery(companyDomain.id);
	const isLoadingPowerBi = !powerBiStatus && isFetchingPowerBi;
	const ewrUrl = reportStatus?.ewrUrl ?? null;
	return {
		access: getProductAccess({
			companyDomain,
			ewrUrl,
			hasExcelReportAccess,
			hasCompanyAdmin: hasPermissions(companyDomain.id, [COMPANYADMIN]),
			aiChat,
			planningModuleCount: planningModules?.length ?? 0,
			intelligenceLayerVisible,
			powerBiConnected: powerBiStatus?.connected ?? false,
		}),
		excelTarget: getExcelTarget(companyDomain, {
			hasExcelReportAccess,
			ewrUrl,
		}),
		ewrUrl,
		loading: {
			ai: aiChat.isLoading,
			planning: isLoadingModules,
			excel: isLoadingReportStatus,
			powerBi: isLoadingPowerBi,
		},
		failed: { excel: isReportStatusError, powerBi: isPowerBiError },
	};
};
